/**
 * Sticky Header 컴포넌트
 * 상세 페이지에서 스크롤해도 상단에 고정되는 액션 바
 *
 * 2026-07-26 web → rc 승격(시뮬레이션 chrome 배포판 동일화) — rc는 router 무의존이라
 * backLink 라우팅은 host 주입(onNavigateBack, web 재수출층이 useNavigate로 공급).
 */
import { buttonClassName } from '@safetyhub/ui';
import type { DetailChromeOverflow } from '@safetyhub/shared';
import { Tooltip } from './Tooltip';
import { useState, useRef, useEffect, useLayoutEffect, forwardRef } from 'react';
import { useDeviceLayout } from './useDeviceLayout';
import { splitDetailChromeByFit } from './detailChromeResolve';
import { ChevronLeftIcon } from './chromeIcons';

export interface StickyHeaderAction {
  key: string;
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'warning' | 'success' | 'ghost';
  disabled?: boolean;
  loading?: boolean;
  /** 변경사항 있음 표시 */
  hasChanges?: boolean;
  icon?: React.ReactNode;
  /** 모바일에서 숨김 (드롭다운에만 표시) */
  hideOnMobile?: boolean;
  /**
   * 좁은 폭에서 접힐 수 있는가 — 레지스트리(`DETAIL_CHROME_ACTIONS`)의 `defaultOverflow`가
   * 실려 온다. 지정된 액션이 하나라도 있으면 헤더 우측이 **실측 폭 기반 접기**로 전환된다
   * (`never`=항상 인라인 / `auto`=들어갈 만큼 / `always`=항상 더보기). 미지정=종전대로 전부 인라인.
   */
  overflow?: DetailChromeOverflow;
  /** ReportWriteShell footer에서 모바일 완전 숨김 (데스크탑은 표시) */
  hiddenOnMobile?: boolean;
  /** 비활성화 시 표시할 툴팁 */
  tooltip?: string;
  /** 모바일에서 사용할 축약 텍스트 (없으면 label 사용) */
  mobileLabel?: string;
  /** 모바일에서 아이콘을 숨기고 라벨만 표시 (아이콘이 모호한 경우 텍스트로 구분) */
  mobileHideIcon?: boolean;
  /** 드롭다운 메뉴 항목 (있으면 이 액션은 메뉴 트리거로 동작 — onClick 대신 메뉴 토글) */
  menuItems?: Array<{
    key: string;
    label: string;
    onClick: () => void;
    variant?: 'danger' | 'secondary';
    disabled?: boolean;
    tooltip?: string;
  }>;
}

export interface StickyHeaderProps {
  /** 뒤로가기 링크 — onNavigateBack(host 라우터)으로 이동. 미주입 시 렌더되지 않음(onBack 사용) */
  backLink?: string;
  /** backLink 클릭 시 라우팅(host 주입 — web 재수출층이 useNavigate(replace)로 공급) */
  onNavigateBack?: (to: string) => void;
  /** sticky top 오프셋(px) — 호스트 상단 고정 요소(시뮬 배너 등) 아래에 붙일 때. 미지정=기존 클래스(top-14 lg:top-0) */
  stickyTopOffset?: number;
  /** 뒤로가기 클릭 핸들러 (backLink 대신 사용) */
  onBack?: () => void;
  /** 뒤로가기 라벨 */
  backLabel?: string;
  /** 제목 (선택적 - 뒤로가기 링크 옆에 표시) */
  title?: string;
  /** 부제목 (선택적 - 제목 아래에 작은 글씨로 표시) */
  subtitle?: string;
  /** 액션 버튼들 */
  actions?: StickyHeaderAction[];
  /** 왼쪽 영역 액션 버튼들 (뒤로가기 링크/제목 옆에 표시) */
  leftActions?: StickyHeaderAction[];
  /** 왼쪽 영역 커스텀 콘텐츠 (뒤로가기/제목 옆, leftActions 앞에 표시 — 상태 배지 등) */
  leftContent?: React.ReactNode;
  /** 오른쪽 영역 커스텀 콘텐츠 (액션 버튼 왼쪽에 표시) */
  rightContent?: React.ReactNode;
  /** 내부 콘텐츠 최대 너비 (Tailwind 클래스). 기본: 'max-w-7xl' */
  maxWidth?: string;
  /** 추가 className */
  className?: string;
  /** 모바일(phone)에서 오른쪽 actions 전부를 ⋯ 더보기 메뉴로 collapse (기본 false — 개별 hideOnMobile 사용) */
  collapseActionsOnMobile?: boolean;
}

export const StickyHeader = forwardRef<HTMLDivElement, StickyHeaderProps>(function StickyHeader({
  backLink,
  onNavigateBack,
  stickyTopOffset,
  onBack,
  backLabel = '목록',
  title,
  subtitle,
  actions = [],
  leftActions = [],
  leftContent,
  rightContent,
  maxWidth = 'max-w-7xl',
  className = '',
  collapseActionsOnMobile = false,
}: StickyHeaderProps, ref) {
  const layout = useDeviceLayout();
  const isPhone = layout === 'phone';
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // 모바일 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMobileMenu(false);
      }
    };

    if (showMobileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMobileMenu]);

  // 시각 출처는 packages/ui `Button` 단일 — 색·radius·높이·포커스 링을 여기서 다시 정의하지 않는다.
  //
  // 이전에는 "앱 버튼과 나란히 서므로 시안의 그라데이션 대신 평면을 따른다"고 적혀 있었는데,
  // 그 근거가 뒤집혔다. `Button` 프리미티브 자체가 시안(`.btn`) 정본으로 재정의되면서
  // **"나란히 선다"는 논리가 이제 그라데이션 쪽을 가리킨다.** 평면을 유지하면 오히려 갈린다.
  //
  // 반응형 패딩(px-2.5 sm:px-4 …)을 버리고 md 고정으로 간 근거: 이 함수를 쓰는 버튼 중
  // 여러 개가 몰리는 오른쪽 액션 묶음은 `!isPhone`이라 폰에서 아예 렌더되지 않고(폰은 아래
  // 모바일 메뉴), 폰에서 렌더되는 leftActions는 모든 호출처에서 최대 1개(결재 이력)다.
  // 좁은 폭에서 넘칠 조건 자체가 성립하지 않는다. 가로로도 md(px-3.5=14px)가 기존
  // 데스크탑(px-4=16px)보다 좁아 데스크탑에서 넘칠 여지는 줄어든다.
  const getButtonClassName = (action: StickyHeaderAction) => {
    const variant = action.variant ?? 'secondary';
    const inactive = action.disabled || action.loading;
    // ⚠️ 비활성 스타일을 **클래스로도** 준다. 아래 tooltip 분기가 `disabled` 속성 대신
    // `aria-disabled`+`pointer-events-none`으로 그리는 버튼을 만드는데(disabled면 mouseenter가
    // 막혀 툴팁이 안 뜨므로), 그 자리엔 `disabled:` variant가 발동하지 않는다.
    // 속성이 실제로 붙는 경우엔 base의 `disabled:*`와 겹칠 뿐 해가 없다.
    // hasChanges 강조 링 + 헤더 줄바꿈 금지도 소비처 사정이라 extra로 얹는다.
    const extra = [
      'whitespace-nowrap',
      inactive ? 'opacity-50 cursor-not-allowed' : '',
      action.hasChanges && variant === 'primary' && !inactive
        ? 'ring-2 ring-brand-300 ring-offset-1'
        : '',
    ]
      .filter(Boolean)
      .join(' ');
    return buttonClassName(variant, 'md', extra);
  };

  const getMobileButtonClassName = (action: StickyHeaderAction) => {
    const base = 'w-full px-4 py-3 text-sm font-medium text-left flex items-center gap-3 transition-colors';

    if (action.disabled || action.loading) {
      return `${base} opacity-50 cursor-not-allowed text-gray-400`;
    }

    switch (action.variant) {
      case 'primary':
        return `${base} text-brand-600 hover:bg-brand-50 ${action.hasChanges ? 'bg-brand-50' : ''}`;
      case 'danger':
        return `${base} text-red-600 hover:bg-red-50`;
      case 'warning':
        return `${base} text-amber-600 hover:bg-amber-50`;
      case 'success':
        return `${base} text-green-600 hover:bg-green-50`;
      default:
        return `${base} text-gray-700 hover:bg-gray-50`;
    }
  };

  // 모바일 주요 버튼 (hideOnMobile 제외한 전체). collapse 정책 시 우측 actions 전부 ⋯ 메뉴로
  const visibleMobileActions = collapseActionsOnMobile ? [] : actions.filter(a => !a.hideOnMobile);
  // 드롭다운: hideOnMobile 액션만 (collapse 정책 시 우측 actions 전부)
  const menuActions = collapseActionsOnMobile ? actions : actions.filter(a => a.hideOnMobile);

  /**
   * 헤더 우측 가용 폭 실측 (2026-09-10).
   *
   * 하단 액션바는 뷰포트 폭이 곧 가용 폭이라 해소기가 미리 접지만, 헤더 우측은 아니다 —
   * 「행 폭 − 좌측(뒤로가기 라벨 + 제목 + 상태 배지)」이고 그 좌측은 진입점마다 라벨이 달라
   * 추정이 반드시 어긋난다. 그래서 DOM을 가진 여기서 잰다.
   *
   * `useLayoutEffect`로 첫 페인트 전에 한 번 재고, 이후는 `ResizeObserver`가 따라간다
   * (창 크기·좌측 라벨 변화 둘 다 잡힌다 — resize 이벤트만으로는 후자를 놓친다).
   */
  const rowRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const [rightAvailable, setRightAvailable] = useState<number | null>(null);
  useLayoutEffect(() => {
    const row = rowRef.current;
    const left = leftRef.current;
    if (!row || !left || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      // 좌우 그룹 사이 간격(gap-4=16px)만큼은 빼 둔다 — 딱 맞게 재면 한 칸 넘친다.
      const GROUP_GAP = 16;
      setRightAvailable(Math.max(0, row.clientWidth - left.offsetWidth - GROUP_GAP));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(row);
    ro.observe(left);
    return () => ro.disconnect();
  }, []);

  /**
   * 헤더 우측 접기 — 레지스트리가 실은 `overflow`가 하나라도 있을 때만 발동한다(미지정
   * 호출처는 종전대로 전부 인라인). 정책 판정은 하단바와 **같은 함수**(`splitDetailChromeByFit`)라
   * 규칙이 두 벌이 되지 않는다.
   *
   * 폭을 아직 못 쟀으면(첫 렌더·ResizeObserver 부재) 접지 않는다 — 없는 폭으로 접으면 화면이
   * 한 번 깜빡이며 줄었다 늘어난다.
   *
   * ⚠️ **0도 「못 잰 것」으로 본다.** 레이아웃 전이거나 숨은 컨테이너(display:none 조상·jsdom)에서는
   * `clientWidth`가 0으로 나오는데, 그걸 그대로 믿으면 폭이 넉넉한 화면에서도 `never`를 뺀 전부가
   * 「더보기」로 접힌다 — 측정 실패가 「좁다」로 둔갑하는 전형이다.
   */
  const headerOverflowEnabled = actions.some((a) => a.overflow !== undefined);
  const { inlineActions, headerMenuActions } = (() => {
    if (!headerOverflowEnabled || rightAvailable === null || rightAvailable <= 0) {
      return { inlineActions: actions, headerMenuActions: [] as StickyHeaderAction[] };
    }
    const { visible, menu } = splitDetailChromeByFit(
      actions.map((a) => ({
        label: typeof a.label === 'string' ? a.label : '',
        overflow: a.overflow ?? 'auto',
      })),
      rightAvailable,
    );
    return {
      inlineActions: visible.map((i) => actions[i]),
      headerMenuActions: menu.map((i) => actions[i]),
    };
  })();

  return (
    <div
      ref={ref}
      className={`sticky top-14 lg:top-0 z-30 bg-gray-50 border-b ${className}`}
      // 인라인 top이 클래스(top-14/lg:top-0)를 덮는다 — 호스트 배너 아래 고정용(미지정=기존 동작).
      style={stickyTopOffset != null ? { top: stickyTopOffset } : undefined}
    >
      <div className={`${maxWidth} mx-auto px-4 sm:px-6 lg:px-8`}>
        <div ref={rowRef} className={`flex items-center justify-between ${title ? 'py-3' : 'h-14'}`}>
          {/* 왼쪽: 뒤로가기 링크 + 제목 */}
          <div ref={leftRef} className="flex items-center gap-4" style={{ minWidth: 0 }}>
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
                style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                /* 뒤로가기는 줄이지도 줄바꿈하지도 않는다 — 인라인 style인 이유는 rc에서 처음 쓰는
                   Tailwind 유틸이 앱 CSS에 미생성돼 조용히 무스타일이 될 수 있어서다(rc 규약).
                   이게 없으면 우측이 붐빌 때 flex가 이 칸부터 줄여 라벨이 «문자 단위»로 무너진다
                   (2026-09-10 폴드 실사고: 「보고서 목록」이 세로로 한 자씩 떨어졌다). */
              >
                <ChevronLeftIcon className="w-4 h-4" />
                <span className="text-sm font-medium">{backLabel}</span>
              </button>
            ) : backLink && onNavigateBack ? (
              <button
                type="button"
                onClick={() => onNavigateBack(backLink)}
                className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
                style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                /* 뒤로가기는 줄이지도 줄바꿈하지도 않는다 — 인라인 style인 이유는 rc에서 처음 쓰는
                   Tailwind 유틸이 앱 CSS에 미생성돼 조용히 무스타일이 될 수 있어서다(rc 규약).
                   이게 없으면 우측이 붐빌 때 flex가 이 칸부터 줄여 라벨이 «문자 단위»로 무너진다
                   (2026-09-10 폴드 실사고: 「보고서 목록」이 세로로 한 자씩 떨어졌다). */
              >
                <ChevronLeftIcon className="w-4 h-4" />
                <span className="text-sm font-medium">{backLabel}</span>
              </button>
            ) : null}

            {/* 제목/부제목 (있는 경우) */}
            {title && (
              <div className="border-l pl-4">
                <h1 className="text-lg font-semibold text-gray-900 leading-tight">{title}</h1>
                {subtitle && (
                  <p className="text-sm text-gray-500">{subtitle}</p>
                )}
              </div>
            )}

            {/* 왼쪽 커스텀 콘텐츠 (상태 배지 등 — leftActions 앞에 표시) */}
            {leftContent}

            {/* 왼쪽 액션 버튼 (뒤로가기/제목 옆) */}
            {leftActions.map((action) => (
              <button
                key={action.key}
                type="button"
                onClick={action.onClick}
                disabled={action.disabled || action.loading}
                className={getButtonClassName(action)}
                title={action.tooltip}
              >
                {action.loading ? (
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : action.icon}
                {action.label}
              </button>
            ))}
          </div>

          {/* 오른쪽: 커스텀 콘텐츠 + 액션 버튼 (desktop·fold = 버튼 전부 노출) */}
          {!isPhone && (
          <div className="flex items-center gap-4">
            {rightContent}

            {/* 데스크탑 액션 버튼들 */}
            {(inlineActions.length > 0 || headerMenuActions.length > 0) && (
              <div className="flex items-center gap-2">
                {inlineActions.map((action) => {
                  const btn = (
                    <button
                      type="button"
                      onClick={action.onClick}
                      disabled={action.disabled || action.loading}
                      className={getButtonClassName(action)}
                    >
                      {action.loading ? (
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : action.icon}
                      {action.label}
                      {action.hasChanges && !action.loading && (
                        <span className="w-2 h-2 bg-orange-500 rounded-full" />
                      )}
                    </button>
                  );

                  if (action.disabled && action.tooltip) {
                    // disabled 버튼은 mouseenter를 차단하므로, disabled 대신 스타일로 비활성화 표현
                    const disabledBtn = (
                      <button
                        type="button"
                        className={`${getButtonClassName(action)} pointer-events-none`}
                        tabIndex={-1}
                        aria-disabled="true"
                      >
                        {action.icon}
                        {action.label}
                      </button>
                    );
                    return (
                      <Tooltip key={action.key} content={action.tooltip} placement="top" align="right">
                        <div className="cursor-help">{disabledBtn}</div>
                      </Tooltip>
                    );
                  }

                  return <div key={action.key}>{btn}</div>;
                })}

                {/* 넘친 액션 — 「더보기」 한 칸으로 묶는다(폰의 ⋯ 메뉴와 같은 마크업·같은 상태).
                    폴드/태블릿에서 위임 5종이 헤더에 몰려도 행이 무너지지 않는다. */}
                {headerMenuActions.length > 0 && (
                  <div className="relative" ref={menuRef}>
                    <button
                      type="button"
                      onClick={() => setShowMobileMenu(!showMobileMenu)}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-control"
                      aria-label="더보기"
                      title="더보기"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                      </svg>
                    </button>
                    {showMobileMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-card shadow-lg border z-50">
                        <div className="py-1">
                          {headerMenuActions.map((action) => (
                            <div key={action.key}>
                              <button
                                type="button"
                                onClick={() => {
                                  action.onClick();
                                  setShowMobileMenu(false);
                                }}
                                disabled={action.disabled || action.loading}
                                className={getMobileButtonClassName(action)}
                              >
                                {action.icon}
                                <span className="flex-1">{action.label}</span>
                              </button>
                              {/* 비활성 사유는 그 자리에서 — 메뉴에서는 툴팁이 닿지 않는다. */}
                              {action.disabled && action.tooltip && (
                                <p className="px-4 pb-2 text-xs text-gray-500">{action.tooltip}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* 모바일(phone) 액션 버튼들 — 보조 액션은 ⋯ 더보기 메뉴 */}
          {isPhone && (
          <div className="flex items-center gap-1.5" ref={menuRef}>
            {/* 주요 액션 버튼 - 1줄 flex */}
            {visibleMobileActions.length > 0 && (
              <div className="flex gap-2">
                {visibleMobileActions.map((action) => (
                  <div key={action.key}>
                    <button
                      type="button"
                      onClick={action.onClick}
                      disabled={action.disabled || action.loading}
                      className={getButtonClassName(action)}
                      title={action.tooltip || action.label}
                    >
                      {action.loading ? (
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : action.icon && !action.mobileHideIcon ? (
                        action.icon
                      ) : null}
                      <span className="text-xs">{action.mobileLabel ?? action.label}</span>
                      {action.hasChanges && !action.loading && (
                        <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* 더보기 메뉴 버튼 */}
            {menuActions.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-control"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </button>

                {/* 드롭다운 메뉴 */}
                {showMobileMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-card shadow-lg border z-50">
                    <div className="py-1">
                      {menuActions.map((action) => (
                        <div key={action.key}>
                          <button
                            type="button"
                            onClick={() => {
                              action.onClick();
                              setShowMobileMenu(false);
                            }}
                            disabled={action.disabled || action.loading}
                            className={getMobileButtonClassName(action)}
                          >
                            {action.icon}
                            <span className="flex-1">{action.label}</span>
                            {action.hasChanges && (
                              <span className="ml-auto w-2 h-2 bg-orange-500 rounded-full" />
                            )}
                          </button>
                          {/* 비활성화 이유 (모바일) */}
                          {action.disabled && action.tooltip && (
                            <p className="px-4 pb-2 text-xs text-gray-500">{action.tooltip}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default StickyHeader;
